/**
 * Gets UTC offset and city name dynamically using Intl.DateTimeFormat
 * @param tz - Timezone in IANA format (e.g., "America/Sao_Paulo")
 * @param locale - User locale for formatting (e.g., "pt-BR", "en-US")
 * @returns Object with utc, city and formatted label
 */
function getUtcOffsetAndCity(tz: string, locale = 'es-419') {
  const now = new Date()

  let utc = null
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset' // e.g.: "GMT-3" or "UTC-03:00"
    }).formatToParts(now)
    const tzName = parts.find((p) => p.type === 'timeZoneName')?.value || ''
    utc = tzName.replace(/^GMT/, 'UTC')
  } catch {
    console.error('Error getting timezone offset for', tz)
  }

  // Fallback: calculate offset manually if shortOffset is not available
  if (!utc) {
    try {
      const offsetMinutes = getTimezoneOffset(tz, now)
      const hours = Math.floor(Math.abs(offsetMinutes) / 60)
      const minutes = Math.abs(offsetMinutes) % 60
      const sign = offsetMinutes <= 0 ? '+' : '-'
      utc =
        minutes > 0
          ? `UTC${sign}${hours}:${minutes.toString().padStart(2, '0')}`
          : `UTC${sign}${hours}`
    } catch {
      utc = 'UTC'
    }
  }

  // Normalize "UTC-03:00" -> "UTC-3" (optional)
  utc = utc.replace(/:00$/, '')

  // 2) Get friendly name in user's locale and extract city
  let city = ''
  try {
    const longGeneric =
      new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        timeZoneName: 'longGeneric'
      })
        .formatToParts(now)
        .find((p) => p.type === 'timeZoneName')?.value || ''

    // Extract city name dynamically based on locale patterns
    city = extractCityFromTimezoneName(longGeneric, locale)
  } catch {
    console.error('Error getting timezone name for', tz)
    city = tz.split('/').pop()?.replace(/_/g, ' ') || tz
  }

  return { utc, city, label: `${utc} (${city})` }
}

/**
 * Calculate timezone offset in minutes for a given timezone
 * @param tz - IANA timezone identifier
 * @param date - Date to calculate offset for
 * @returns Offset in minutes (negative for ahead of UTC)
 */
function getTimezoneOffset(tz: string, date: Date): number {
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }))
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone: tz }))
  return (utcDate.getTime() - tzDate.getTime()) / (1000 * 60)
}

/**
 * Extract city name from timezone display name based on locale patterns
 * @param timezoneName - Full timezone name (e.g., "Horário de Brasília", "Eastern Standard Time")
 * @param locale - User locale
 * @returns Extracted city name
 */
function extractCityFromTimezoneName(
  timezoneName: string,
  locale: string
): string {
  if (!timezoneName) return ''

  // Define patterns for different locales
  const patterns: Record<string, RegExp[]> = {
    'pt-BR': [
      /^Horário\s*(padrão\s*)?de\s*(.+)$/i,
      /^Hora\s*(padrão\s*)?de\s*(.+)$/i
    ],
    es: [
      /^Hora\s*(estándar\s*)?de\s*(.+)$/i,
      /^Horario\s*(estándar\s*)?de\s*(.+)$/i
    ],
    en: [/^(.+)\s+(Standard|Daylight|Summer)\s+Time$/i, /^(.+)\s+Time$/i],
    de: [/^(.+)zeit$/i, /^(.+)\s+(Normal|Sommer)zeit$/i]
  }

  // Get patterns for the locale (fallback to base language)
  const baseLocale = locale.split('-')[0]
  const localePatterns =
    patterns[locale] || patterns[baseLocale] || patterns['en']

  // Try to extract city using locale-specific patterns
  for (const pattern of localePatterns) {
    const match = timezoneName.match(pattern)
    if (match) {
      // For pt-BR patterns, return the captured group (city name)
      if (locale.startsWith('pt') && match[2]) {
        return match[2].trim()
      }
      // For other patterns, return the first captured group
      if (match[1]) {
        return match[1].trim()
      }
    }
  }

  const cleaned = timezoneName
    .replace(/\s+(Time|Zeit|Hora|Horário)$/i, '')
    .trim()
  return cleaned || timezoneName
}

/**
 * Gets formatted timezone display using dynamic UTC offset and city name
 * @param timezone - Timezone in IANA format (e.g., "America/Sao_Paulo")
 * @param locale - User locale for formatting (e.g., "pt-BR", "en-US", "de-DE")
 * @returns Formatted timezone string (e.g., "UTC-3 (Brasília)")
 */
export const getFormattedTimezone = (
  timezone: string,
  locale = 'en-US'
): string => {
  if (!timezone) {
    return timezone
  }

  try {
    const { label } = getUtcOffsetAndCity(timezone, locale)
    return label
  } catch (error) {
    // Fallback to original timezone if error occurs
    return timezone
  }
}

/**
 * Gets the UTC offset and city information for a timezone
 * @param timezone - Timezone in IANA format (e.g., "America/Sao_Paulo")
 * @param locale - User locale for formatting (e.g., "pt-BR", "en-US", "de-DE")
 * @returns Object with utc offset, city name and formatted label
 */
export const getTimezoneInfo = (timezone: string, locale = 'en-US') => {
  if (!timezone) {
    return { utc: '', city: '', label: timezone }
  }

  try {
    return getUtcOffsetAndCity(timezone, locale)
  } catch (error) {
    return { utc: '', city: '', label: timezone }
  }
}
